export const FORM_TYPE_ALIASES = {
  purchase_agreement: 'purchase_agreement',
  listing_agreement: 'listing_agreement',
  buyer_agreement: 'buyer_agreement',
  buyer_broker_service_agreement: 'buyer_agreement',
  contract: 'purchase_agreement',
  listing: 'listing_agreement',
  'buyer-agreement': 'buyer_agreement',
  sellers_counteroffer: 'sellers_counteroffer',
  buyers_counteroffer: 'buyers_counteroffer',
  buyers_counteroffer_to_sellers_counteroffer: 'buyers_counteroffer',
  addendum: 'addendum',
  escalation_addendum: 'escalation_addendum',
  notice_of_cancellation: 'notice_of_cancellation',
  noc: 'notice_of_cancellation',
};

export const PORTAL_PATH_BY_FORM_TYPE = {
  purchase_agreement: 'contract',
  listing_agreement: 'listing',
  buyer_agreement: 'buyer-agreement',
  sellers_counteroffer: 'contract',
  buyers_counteroffer: 'contract',
  addendum: 'contract',
  escalation_addendum: 'contract',
  notice_of_cancellation: 'contract',
};

export const PARENT_PATH_BY_TYPE = {
  contract: 'contract',
  listing: 'listing',
  buyer_agreement: 'buyer-agreement',
};

export function canonicalFormType(id) {
  const key = String(id || '').trim().toLowerCase();
  return FORM_TYPE_ALIASES[key] || key;
}

export const FORM_TYPE_CATALOG = [
  {
    formType: 'purchase_agreement',
    label: 'Purchase Agreement',
    description: 'Create a new buyer-side purchase contract',
    icon: '🏡',
    color: '#7fb069',
    catalogGroup: 'core',
  },
  {
    formType: 'buyer_agreement',
    label: 'Buyers Agreement',
    description: 'Create a Buyer Broker Service Agreement',
    icon: '🤝',
    color: '#D4A574',
    catalogGroup: 'core',
  },
  {
    formType: 'listing_agreement',
    label: 'Listing Agreement',
    description: 'Create a new seller-side listing',
    icon: '📋',
    color: '#7fb069',
    catalogGroup: 'core',
  },
];

const COUNTER_WIZARD_STEPS = [
  { step_id: 'parent', fields: ['parent_contract_id', 'parent_listing_id', 'parent_buyer_agreement_id'] },
  { step_id: 'purchase_price', fields: ['purchase_price'], input: 'number' },
  { step_id: 'acceptance_deadline', fields: ['acceptance_deadline'], input: 'datetime' },
  { step_id: 'other_terms', fields: ['other_terms'], input: 'textarea', optional: true },
];

export const ESCALATION_WIZARD_STEPS = [
  { step_id: 'parent', fields: ['parent_buyer_agreement_id', 'parent_contract_id'] },
  {
    step_id: 'property_address',
    fields: ['property_address'],
    prompt: 'Confirm the property address for this offer.',
  },
  {
    step_id: 'seller_name',
    fields: ['seller_name'],
    prompt: 'Confirm the seller names printed on this Escalation Addendum.',
  },
  {
    step_id: 'purchase_price_terms',
    fields: ['escalation_increment', 'max_purchase_price'],
    field_inputs: {
      escalation_increment: 'number',
      max_purchase_price: 'number',
    },
    prompt: 'Set the escalation increment and the maximum purchase price.',
  },
  {
    step_id: 'financing_procedure',
    fields: [
      'financing_procedure',
      'financing_loan_percent',
      'financing_loan_cap',
      'financing_other',
    ],
    input: 'radio',
    options: [
      { value: '', label: 'Buyer is not financing (leave this section blank)' },
      { value: 'loan_stays', label: 'Loan amount stays the same; Buyer pays any purchase-price increase' },
      { value: 'down_stays', label: 'Down payment stays the same; any increase is added to the loan' },
      { value: 'loan_percent', label: 'Loan automatically increases to this percent of the new purchase price' },
      { value: 'loan_cap', label: 'Loan shall not exceed this dollar amount' },
      { value: 'other', label: 'Other financing terms' },
    ],
    field_inputs: {
      financing_procedure: 'radio',
      financing_loan_percent: 'number',
      financing_loan_cap: 'number',
      financing_other: 'textarea',
    },
    show_when: {
      financing_loan_percent: { field: 'financing_procedure', equals: 'loan_percent' },
      financing_loan_cap: { field: 'financing_procedure', equals: 'loan_cap' },
      financing_other: { field: 'financing_procedure', equals: 'other' },
    },
    require_when: {
      financing_loan_percent: { field: 'financing_procedure', equals: 'loan_percent' },
      financing_loan_cap: { field: 'financing_procedure', equals: 'loan_cap' },
      financing_other: { field: 'financing_procedure', equals: 'other' },
    },
    prompt: 'If Buyer will be financing this purchase, choose one financing procedure. The official form asks you to check one option in this section.',
  },
  {
    step_id: 'competing_offer_closing_days',
    fields: ['competing_offer_closing_days'],
    input: 'number',
    optional: true,
    prompt: 'If a competing offer has a later closing, how many days after contract acceptance must that closing occur? Leave blank to keep the printed default of 60.',
  },
  {
    step_id: 'acceptance_method',
    fields: ['acceptance_method', 'acceptance_other'],
    input: 'radio',
    options: [
      { value: 'ratified', label: 'This Escalation Addendum is ratified when signed by all parties' },
      { value: 'counteroffer', label: "Seller will submit a Seller's Counteroffer" },
      { value: 'other', label: 'Other acceptance terms' },
    ],
    field_inputs: {
      acceptance_method: 'radio',
      acceptance_other: 'textarea',
    },
    show_when: {
      acceptance_other: { field: 'acceptance_method', equals: 'other' },
    },
    require_when: {
      acceptance_other: { field: 'acceptance_method', equals: 'other' },
    },
    prompt: 'Choose how this Escalation Addendum will be accepted (check one).',
  },
];

export const OTHER_FORM_CATALOG = [
  {
    group: 'negotiation',
    label: 'Negotiation',
    forms: [
      {
        formType: 'sellers_counteroffer',
        label: "Seller's Counteroffer",
        description: 'Counter a buyer offer from a listing or purchase contract',
        icon: '↩️',
        color: '#7fb069',
        parentContexts: ['contract', 'listing'],
        createsEntity: false,
        wizardSteps: COUNTER_WIZARD_STEPS,
      },
      {
        formType: 'buyers_counteroffer',
        label: "Buyer's Counteroffer",
        description: 'Counter a seller counter from a BBSA or purchase contract',
        icon: '↪️',
        color: '#D4A574',
        parentContexts: ['contract', 'buyer_agreement'],
        createsEntity: false,
        wizardSteps: COUNTER_WIZARD_STEPS,
      },
      {
        formType: 'addendum',
        label: 'Addendum',
        description: 'Change terms on an in-progress purchase contract',
        icon: '📝',
        color: '#6b8cae',
        parentContexts: ['contract'],
        createsEntity: false,
        wizardSteps: [
          { step_id: 'parent', fields: ['parent_contract_id'] },
          { step_id: 'addendum_body', fields: ['addendum_body'], input: 'textarea' },
        ],
      },
      {
        formType: 'notice_of_cancellation',
        label: 'Notice of Cancellation',
        description: 'Cancel an existing purchase agreement',
        icon: '🚫',
        color: '#c45c5c',
        parentContexts: ['contract'],
        createsEntity: false,
        wizardSteps: [
          { step_id: 'parent', fields: ['parent_contract_id'] },
          {
            step_id: 'cancelling_side',
            fields: ['cancelling_side'],
            input: 'radio',
            prompt: 'Who is cancelling this purchase agreement?',
            options: [
              { value: 'buyer', label: "Buyer's notice of cancellation" },
              { value: 'seller', label: "Seller's notice of cancellation" },
            ],
          },
          {
            step_id: 'buyer_reason',
            fields: ['cancellation_reason'],
            input: 'radio',
            options: [
              { value: 'inspections_unsatisfactory', label: "Buyer's inspections or reviews are unsatisfactory" },
              { value: 'title_objections', label: 'Seller failed to cure title objections' },
              { value: 'seller_breach', label: 'Seller breached or failed to close' },
              { value: 'appraisal_short', label: 'The property appraised for less than the purchase price' },
              { value: 'loan_terms_unacceptable', label: 'Loan terms were unobtainable or unacceptable' },
              { value: 'final_financing_failed', label: 'Buyer could not secure final financing' },
              { value: 'additional_provisions', label: 'Additional provisions in the contract or a supplement' },
            ],
            show_when: {
              cancellation_reason: { field: 'cancelling_side', equals: 'buyer' },
            },
          },
          {
            step_id: 'seller_reason',
            fields: ['cancellation_reason'],
            input: 'radio',
            options: [
              { value: 'earnest_money_not_delivered', label: 'Buyer did not deliver earnest money in time' },
              { value: 'buyer_breach', label: 'Buyer breached or failed to close' },
              { value: 'loan_approval_notice_missing', label: 'Seller did not receive written loan-approval notice in time' },
              { value: 'repairs_agreement_not_reached', label: 'No written repair agreement was reached in time' },
            ],
            show_when: {
              cancellation_reason: { field: 'cancelling_side', equals: 'seller' },
            },
          },
        ],
      },
      {
        formType: 'escalation_addendum',
        label: 'Escalation Addendum',
        description: 'Escalate an offer against competing offers',
        icon: '📈',
        color: '#6b8cae',
        parentContexts: ['buyer_agreement', 'contract'],
        createsEntity: false,
        wizardSteps: ESCALATION_WIZARD_STEPS,
        optionalWizardFields: [
          'financing_procedure',
          'financing_loan_percent',
          'financing_loan_cap',
          'financing_other',
          'competing_offer_closing_days',
          'acceptance_other',
        ],
      },
    ],
  },
  {
    group: 'listing',
    label: 'Listing',
    forms: [
      {
        formType: 'listing_agreement_amend',
        label: 'Listing Agreement Amendment',
        description: 'Change price, dates, or other terms on an existing listing',
        icon: '✏️',
        color: '#7fb069',
        parentContexts: ['listing'],
        createsEntity: false,
        wizardSteps: [
          { step_id: 'parent', fields: ['parent_listing_id'] },
          { step_id: 'listing_price', fields: ['listing_price'], input: 'number' },
          { step_id: 'expiration_date', fields: ['expiration_date'], input: 'date' },
          { step_id: 'other_terms', fields: ['other_terms'], input: 'textarea', optional: true },
        ],
        optionalWizardFields: ['listing_price', 'expiration_date', 'other_terms'],
      },
      {
        formType: 'listing_agreement_extension',
        label: 'Listing Agreement Extension',
        description: 'Extend the expiration date on an existing listing',
        icon: '📆',
        color: '#7fb069',
        parentContexts: ['listing'],
        createsEntity: false,
        wizardSteps: [
          { step_id: 'parent', fields: ['parent_listing_id'] },
          { step_id: 'expiration_date', fields: ['expiration_date'], input: 'date' },
        ],
      },
    ],
  },
  {
    group: 'buyer',
    label: 'Buyer',
    forms: [
      {
        formType: 'buyer_agreement_amend',
        label: 'Buyer Agreement Amendment',
        description: 'Change commission, dates, or other terms on an existing buyer agreement',
        icon: '✏️',
        color: '#D4A574',
        parentContexts: ['buyer_agreement'],
        createsEntity: false,
        wizardSteps: [
          { step_id: 'parent', fields: ['parent_buyer_agreement_id'] },
          { step_id: 'expiration_date', fields: ['expiration_date'], input: 'date' },
          { step_id: 'buyers_agent_commission_percent', fields: ['buyers_agent_commission_percent'], input: 'number' },
          { step_id: 'buyers_agent_commission_amount', fields: ['buyers_agent_commission_amount'], input: 'number' },
          { step_id: 'other_terms', fields: ['other_terms'], input: 'textarea', optional: true },
        ],
        optionalWizardFields: [
          'expiration_date',
          'buyers_agent_commission_percent',
          'buyers_agent_commission_amount',
          'other_terms',
        ],
      },
      {
        formType: 'buyer_agreement_extension',
        label: 'Buyer Agreement Extension',
        description: 'Extend the expiration date on an existing buyer agreement',
        icon: '📆',
        color: '#D4A574',
        parentContexts: ['buyer_agreement'],
        createsEntity: false,
        wizardSteps: [
          { step_id: 'parent', fields: ['parent_buyer_agreement_id'] },
          { step_id: 'expiration_date', fields: ['expiration_date'], input: 'date' },
        ],
      },
    ],
  },
  {
    group: 'disclosures',
    label: 'Disclosures',
    forms: [
      {
        formType: 'appendix_a_residential_property_condition',
        label: 'Appendix A — Property Condition',
        description: 'Use when the seller will complete the property-condition questionnaire.',
        icon: '🏠',
        color: '#8e6bb0',
        parentContexts: ['listing', 'contract'],
        createsEntity: false,
        allowEarlySubmitAfter: 'header',
        wizardSteps: [
          { step_id: 'parent', fields: ['parent_listing_id', 'parent_contract_id'] },
          { step_id: 'header', fields: ['year_built'] },
        ],
      },
      {
        formType: 'rpcd_exemption',
        label: 'RPCD Exemption',
        description: 'Use when the sale is exempt from the property-condition questionnaire.',
        icon: '⏭️',
        color: '#8e6bb0',
        parentContexts: ['listing', 'contract'],
        createsEntity: false,
        wizardSteps: [
          { step_id: 'parent', fields: ['parent_listing_id', 'parent_contract_id'] },
          {
            step_id: 'exemption',
            fields: ['exemption_reason'],
            input: 'radio',
            options: [
              { value: '1', label: '1. Court order / eminent domain / partition' },
              { value: '2', label: '2. Foreclosure / deed in lieu' },
              { value: '3', label: '3. Fiduciary estate / trust transfer' },
              { value: '4', label: '4. Co-owner to co-owner' },
              { value: '5', label: '5. Spouse or lineal heir' },
              { value: '6', label: '6. Divorce / property settlement' },
              { value: '7', label: '7. Merger / corporate reorganization' },
              { value: '8', label: '8. Governmental entity' },
              { value: '9', label: '9. Newly constructed, never occupied' },
            ],
          },
        ],
      },
      {
        formType: 'appendix_b_rpc_disclaimer',
        label: 'Appendix B — Property Condition Disclaimer',
        description: 'Use when the seller will not complete a property-condition questionnaire.',
        icon: '📄',
        color: '#8e6bb0',
        parentContexts: ['listing', 'contract'],
        createsEntity: false,
        wizardSteps: [
          { step_id: 'parent', fields: ['parent_listing_id', 'parent_contract_id'] },
        ],
      },
      {
        formType: 'lead_based_paint_seller_disclosure',
        label: 'Lead-Based Paint Seller Disclosure',
        description: 'Federal / OREC lead-based paint seller disclosure',
        icon: '🪣',
        color: '#8e6bb0',
        parentContexts: ['listing', 'contract'],
        createsEntity: false,
        wizardSteps: [
          { step_id: 'parent', fields: ['parent_listing_id', 'parent_contract_id'] },
          {
            step_id: 'seller_disclosure',
            fields: ['lbp_presence', 'lbp_presence_explain', 'lbp_records', 'lbp_records_list', 'lbp_inspection'],
            field_inputs: {
              lbp_presence: 'radio',
              lbp_presence_explain: 'textarea',
              lbp_records: 'radio',
              lbp_records_list: 'textarea',
              lbp_inspection: 'radio',
            },
            field_options: {
              lbp_presence: [
                { value: 'known', label: 'Known hazards present' },
                { value: 'unknown', label: 'Seller has no knowledge' },
              ],
              lbp_records: [
                { value: 'provided', label: 'Records provided' },
                { value: 'none', label: 'No records' },
              ],
              lbp_inspection: [
                { value: 'inspect', label: 'Purchaser will inspect' },
                { value: 'waive', label: 'Purchaser waived inspection' },
              ],
            },
            show_when: {
              lbp_presence_explain: { field: 'lbp_presence', equals: 'known' },
              lbp_records_list: { field: 'lbp_records', equals: 'provided' },
              lbp_inspection: { field: 'parent_contract_id', present: true },
            },
            require_when: {
              lbp_presence_explain: { field: 'lbp_presence', equals: 'known' },
              lbp_records_list: { field: 'lbp_records', equals: 'provided' },
              lbp_inspection: { field: 'parent_contract_id', present: true },
            },
          },
        ],
      },
      {
        formType: 'disclosure_of_brokerage_services_seller',
        label: 'Disclosure of Brokerage Duties — Seller',
        description: 'Standalone seller brokerage-duties disclosure',
        icon: '📋',
        color: '#8e6bb0',
        parentContexts: ['listing', 'contract'],
        createsEntity: false,
        wizardSteps: [
          { step_id: 'parent', fields: ['parent_listing_id', 'parent_contract_id'] },
        ],
      },
      {
        formType: 'disclosure_of_brokerage_services_buyer',
        label: 'Disclosure of Brokerage Duties — Buyer',
        description: 'Standalone buyer brokerage-duties disclosure',
        icon: '📋',
        color: '#8e6bb0',
        parentContexts: ['buyer_agreement', 'contract'],
        createsEntity: false,
        wizardSteps: [
          { step_id: 'parent', fields: ['parent_buyer_agreement_id', 'parent_contract_id'] },
        ],
      },
    ],
  },
];

function catalogFallbackCard(formType) {
  for (const group of OTHER_FORM_CATALOG) {
    const match = (group.forms || []).find((form) => form.formType === formType);
    if (match) {
      return match;
    }
  }
  return null;
}

export function catalogFormToCard(item) {
  const fallback = catalogFallbackCard(item.form_type);
  return {
    formType: item.form_type,
    label: item.display_name,
    description: item.catalog_description || item.display_name,
    icon: item.catalog_icon || '📄',
    color: item.catalog_color || '#6b8cae',
    parentContexts: item.parent_contexts || [],
    wizardSteps: item.wizard_steps || [],
    optionalWizardFields: item.optional_wizard_fields || [],
    stillNeededLabels: item.still_needed_labels || {},
    allowEarlySubmitAfter: item.allow_early_submit_after || fallback?.allowEarlySubmitAfter || '',
    createsEntity: item.creates_entity === true,
  };
}

export function catalogGroupsFromPayload(payload) {
  const other = payload?.other || {};
  return Object.entries(other).map(([group, forms]) => ({
    group,
    label: (
      group === 'negotiation' ? 'Negotiation'
        : group === 'listing' ? 'Listing'
          : group === 'buyer' ? 'Buyer'
            : group === 'disclosures' ? 'Disclosures'
              : group.replace(/_/g, ' ')
    ),
    forms: (forms || []).map(catalogFormToCard),
  }));
}

export function formsForParent(parentType, catalog = OTHER_FORM_CATALOG) {
  const parent = parentType === 'purchase_agreement' ? 'contract' : parentType;
  const child = catalog.flatMap((group) => (
    group.forms.filter((form) => (form.parentContexts || []).includes(parent))
  ));
  if (parent === 'buyer_agreement') {
    const purchase = FORM_TYPE_CATALOG.find((form) => form.formType === 'purchase_agreement');
    return purchase ? [purchase, ...child] : child;
  }
  return child;
}

const PARENT_SCOPED_CHILD_FORMS = new Set([
  'listing_agreement_amend',
  'listing_agreement_extension',
  'buyer_agreement_amend',
  'buyer_agreement_extension',
  'appendix_a_residential_property_condition',
  'rpcd_exemption',
  'appendix_b_rpc_disclaimer',
  'lead_based_paint_seller_disclosure',
  'disclosure_of_brokerage_services_seller',
  'disclosure_of_brokerage_services_buyer',
]);

export function childFormAppliesToPurchaseAgreement(formType) {
  return !PARENT_SCOPED_CHILD_FORMS.has(canonicalFormType(formType));
}

export function isChildFormType(formType, catalog = OTHER_FORM_CATALOG) {
  const canonical = canonicalFormType(formType);
  if (!canonical || FORM_TYPE_CATALOG.some((form) => form.formType === canonical)) {
    return false;
  }
  const groups = catalog && catalog.length ? catalog : OTHER_FORM_CATALOG;
  if (groups.some((group) => (
    (group.forms || []).some((form) => form.formType === canonical && form.createsEntity !== true)
  ))) {
    return true;
  }
  if (FORM_TYPE_ALIASES[canonical] === canonical) return true;
  return OTHER_FORM_CATALOG.some((group) => (
    (group.forms || []).some((form) => form.formType === canonical)
  ));
}

export function parentIdFromValues(values = {}) {
  return values.parent_contract_id
    || values.parent_listing_id
    || values.parent_buyer_agreement_id
    || values.contract_id
    || values.listing_id
    || values.buyer_agreement_id
    || null;
}

export function displayNameForFormType(formType, catalog = OTHER_FORM_CATALOG) {
  const canonical = canonicalFormType(formType);
  const core = FORM_TYPE_CATALOG.find((form) => form.formType === canonical);
  if (core) return core.label;
  for (const group of catalog) {
    const match = (group.forms || []).find((form) => form.formType === canonical);
    if (match) return match.label;
  }
  return canonical;
}
